import React, { useState, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { menuConfig } from '../../../config/menuConfig'
import { MenuItem } from '../../../types/layout'
import SidebarMenuItem from './SidebarMenuItem'
import { useMenuState } from '../../../hooks/layout'

interface SidebarMenuProps {
  isCollapsed: boolean
}

/**
 * Componente que renderiza la lista de menús del sidebar
 * Filtra menús según permisos del usuario
 */
const SidebarMenu: React.FC<SidebarMenuProps> = ({ isCollapsed }) => {
  const location = useLocation()
  const { hasPermission } = useAuth()
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)

  // Filtrar menús según permisos
  const filteredMenus = useMemo(() => {
    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
      return items
        .filter((item) => {
          // Si tiene permiso requerido, verificar
          if (item.permission && !hasPermission(item.permission)) {
            return false
          }
          return true
        })
        .map((item) => {
          // Filtrar submenús también
          if (item.submenu) {
            const filteredSubmenu = filterMenuItems(item.submenu)
            return { ...item, submenu: filteredSubmenu }
          }
          return item
        })
    }

    return filterMenuItems(menuConfig)
  }, [hasPermission])

  const { toggleMenu, isMenuOpen } = useMenuState(filteredMenus)

  // Verificar si una ruta está activa
  const isPathActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path)
  }

  return (
    <nav className={`mt-6 flex-1 ${!isCollapsed ? 'overflow-y-auto overflow-x-hidden sidebar-scrollbar' : ''}`}>
      <ul className="space-y-1 pb-20">
        {filteredMenus.map((item) => {
          const isActive = isPathActive(item.path)
          const isOpen = isMenuOpen(item.id)

          return (
            <SidebarMenuItem
              key={item.id}
              item={item}
              isCollapsed={isCollapsed}
              isActive={isActive}
              isOpen={isOpen}
              onToggle={() => toggleMenu(item.id)}
              hoveredItem={hoveredItem}
              onHover={setHoveredItem}
            />
          )
        })}
      </ul>
    </nav>
  )
}

export default React.memo(SidebarMenu)

